import { Box } from '@mui/material';
import { GridActionsCellItemProps, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { H1, usersColumnVisibilityModel, UsersTable } from 'src/components';
import config from 'src/config';
import { PermissionEnum } from 'src/shared/enums/permission.enum';
import { UserStatus } from 'src/shared/enums/userStatus.enum';
import { UserType } from 'src/types';
import emptyUsers from 'src/assets/emptyUsers.svg';
import { setTenantUserStatus } from 'src/redux/tenants/actions';
import { useDispatch } from 'react-redux';
import StyledActionItem from 'src/components/Table/TableComponents/StyledActionItem';
import { useQueryParam } from 'src/hooks';
import { useNavigate } from 'react-router-dom';
import { setParams } from 'src/utils';
import { PATHS } from 'src/navigation';
import { RoleSlug } from 'src/shared/enums/roleSlug.enum';

type RegisteredUsersTabProps = {
  users: UserType[];
};

const RegisteredUsersTab = ({ users }: RegisteredUsersTabProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userId, setUserId] = useQueryParam('id', '');

  const onSetNewStatus = (user: UserType, status: UserStatus) => {
    dispatch(setTenantUserStatus.init({ uuid: user.uuid, status }));
  };

  const navigateToUserProfile = (id: string) => {
    setUserId(id);
    navigate(setParams(PATHS.USER_PROFILE_PAGE, { uuid: id }));
  };

  const goTo = (params: GridRenderCellParams) => {
    navigateToUserProfile(params.row.uuid);
  };

  const columnsVisibility = {
    ...usersColumnVisibilityModel,
    firstName: true,
    lastName: true,
    registrationDate: true,
    userRoles: true,
    status: true,
  };

  const getActions = (params: GridRowParams<any>): React.ReactElement<GridActionsCellItemProps>[] => {
    const user = params.row as UserType;
    if (user.roles?.[0]?.slug === RoleSlug.SYSTEM_ADMIN) {
      return [];
    }

    if (user.roles?.[0]?.slug === RoleSlug.SYSTEM_MANAGER) {
      if (params.row.status === UserStatus.ACTIVE) {
        return config.can(PermissionEnum.BLOCK_USER)
          ? [<StyledActionItem key="blockCell" label="Block" onClick={() => onSetNewStatus(params.row, UserStatus.BLOCKED)} showInMenu />]
          : [];
      }

      if (params.row.status === UserStatus.BLOCKED) {
        return config.can(PermissionEnum.ACTIVATE_USER)
          ? [<StyledActionItem key="activateCell" label="Activate" onClick={() => onSetNewStatus(params.row, UserStatus.ACTIVE)} showInMenu />]
          : [];
      }
    }

    const actions: { [key: number]: any } = {
      [UserStatus.DEACTIVATED]: () => [
        config.can(PermissionEnum.APPROVE_USER) ? (
          <StyledActionItem key="approveCell" label="Approve" onClick={() => onSetNewStatus(params.row, UserStatus.APPROVED)} showInMenu />
        ) : null,
        config.can(PermissionEnum.BLOCK_USER) ? (
          <StyledActionItem key="blockCell" label="Block" onClick={() => onSetNewStatus(params.row, UserStatus.BLOCKED)} showInMenu />
        ) : null,
      ],
      [UserStatus.APPROVED]: () => [
        config.can(PermissionEnum.ACTIVATE_USER) ? (
          <StyledActionItem key="activateCell" label="Activate" onClick={() => onSetNewStatus(params.row, UserStatus.ACTIVE)} showInMenu />
        ) : null,
        config.can(PermissionEnum.BLOCK_USER) ? (
          <StyledActionItem key="blockCell" label="Block" onClick={() => onSetNewStatus(params.row, UserStatus.BLOCKED)} showInMenu />
        ) : null,
      ],
      [UserStatus.ACTIVE]: () => [
        config.can(PermissionEnum.BLOCK_USER) ? (
          <StyledActionItem key="blockCell" label="Block" onClick={() => onSetNewStatus(params.row, UserStatus.BLOCKED)} showInMenu />
        ) : null,
      ],
      [UserStatus.BLOCKED]: () => [
        config.can(PermissionEnum.UNBLOCK_USER) ? (
          <StyledActionItem key="unblockCell" label="Unblock" onClick={() => onSetNewStatus(params.row, UserStatus.DEACTIVATED)} showInMenu />
        ) : null,
      ],
    };

    return actions[params.row.status]().filter((item: any) => item !== null) ?? [];
  };

  return (
    <Box component="main">
      {users?.length ? (
        <Box component="main" sx={{ height: 'calc(100vh - 100px)', pt: 4, width: '100%', overflowX: 'auto' }}>
          <UsersTable
            statePersistId="s360_users_table"
            users={users}
            columnVisibilityModel={columnsVisibility}
            getActions={getActions}
            toolbarProps={{ options: 'mini' }}
            goTo={goTo}
            showGoToForRow={(row) => row.roles.some((role: { slug: string }) => role.slug !== RoleSlug.SYSTEM_ADMIN)}
            idToScroll={userId}
            hiddenColumns={{ favorite: true, clearStatus: true, localUnion: true, unionInfoStatus: true }}
            onCellClick={(params) => {
              const isClickable = params.row.roles?.some((role: { slug: string }) => role.slug !== RoleSlug.SYSTEM_ADMIN);
              const isNameField = params.field === 'firstName' || params.field === 'lastName';

              if (isClickable && isNameField) {
                navigateToUserProfile(params.row.uuid);
              }
            }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            mt: 14,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ ml: 1 }}>
            <img src={emptyUsers} alt="Empty list" />
          </Box>
          <H1 sx={{ mb: 3 }}>{t('usersPage.noUsers')}</H1>
        </Box>
      )}
    </Box>
  );
};
export default RegisteredUsersTab;
